import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import * as htmlToImage from "html-to-image";
import jsPDF from "jspdf";
import { api, fileUrl } from "../lib/api";
import { uid, scaleElements, newImageElement } from "../lib/constants";
import { loadDesignFonts } from "../lib/fontLoader";
import CanvasElement from "../components/editor/CanvasElement";
import PropertiesPanel from "../components/editor/PropertiesPanel";
import AudioTrackBar from "../components/editor/AudioTrackBar";
import TopBar from "../components/editor/TopBar";
import ResizeModal from "../components/editor/ResizeModal";
import SaveTemplateModal from "../components/editor/SaveTemplateModal";
import SaveToStylesModal from "../components/editor/SaveToStylesModal";
import FindReplaceModal from "../components/editor/FindReplaceModal";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import LayersPanel from "../components/editor/LayersPanel";
import MultiSelectPanel from "../components/editor/MultiSelectPanel";
import { TemplatesPanel, ElementsPanel, TextPanel, UploadsPanel, AIPanel, OverlaysPanel } from "../components/editor/LeftPanels";
import { toast } from "sonner";
import {
  Shapes, TextT, UploadSimple, MagicWand, X, Square as LayoutSquare, Sparkle,
} from "@phosphor-icons/react";

const TOOLS = [
  { id: "templates", icon: LayoutSquare, label: "Templates" },
  { id: "elements", icon: Shapes, label: "Elements" },
  { id: "text", icon: TextT, label: "Text" },
  { id: "overlays", icon: Sparkle, label: "Overlays" },
  { id: "uploads", icon: UploadSimple, label: "Uploads" },
  { id: "ai", icon: MagicWand, label: "AI" },
];

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [design, setDesign] = useState(null);
  const [elements, setElements] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  const setSelectedId = (id) => setSelectedIds(id == null ? [] : [id]);
  const groupOriginRef = useRef(null);
  const lastNudgeRef = useRef(0);
  const [activeTool, setActiveTool] = useState("templates");
  const [panelOpen, setPanelOpen] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [fitScale, setFitScale] = useState(0.3);
  const [saving, setSaving] = useState(false);
  const [editingText, setEditingText] = useState(false);
  const [wordSel, setWordSel] = useState(null); // { elId, start, end } — active text selection for per-word styling
  const [templates, setTemplates] = useState([]);
  const [guides, setGuides] = useState({ v: [], h: [] });
  const [interacting, setInteracting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [videoPosters, setVideoPosters] = useState({});
  const [videoBaseCapture, setVideoBaseCapture] = useState(false);
  const [resizeOpen, setResizeOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [saveStylesOpen, setSaveStylesOpen] = useState(false);
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const { user } = useAuth();
  const { isPro, openUpgrade } = useSubscription();
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoExporting, setVideoExporting] = useState(false);
  const [layersOpen, setLayersOpen] = useState(true);
  const [performanceMode, setPerformanceMode] = useState(() => {
    try { return localStorage.getItem("ed_perf_mode") === "1"; } catch { return false; }
  });
  const simplify = (performanceMode || interacting) && !exporting;

  // Lazy-load the full design font library only when the editor opens.
  useEffect(() => { loadDesignFonts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const togglePerformance = useCallback(() => {
    setPerformanceMode((p) => {
      const next = !p;
      try { localStorage.setItem("ed_perf_mode", next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);
  const toggleLayersOpen = useCallback(() => setLayersOpen((o) => !o), []);

  const past = useRef([]);
  const future = useRef([]);
  const [, forceRerender] = useState(0);
  const artboardRef = useRef(null);
  const viewportRef = useRef(null);
  const saveTimer = useRef(null);
  const thumbTimer = useRef(null);
  const thumbBusy = useRef(false);
  const lastThumbSig = useRef("");
  const layersSnapshot = useRef([]);
  const loaded = useRef(false);

  const scale = fitScale * zoom;

  // ---- Load design + templates ----
  useEffect(() => {
    const load = async () => {
      try {
        const [d, t] = await Promise.all([api.get(`/designs/${id}`), api.get("/templates")]);
        setDesign(d.data);
        setElements(d.data.elements || []);
        setFolders(d.data.folders || []);
        setTemplates(t.data);
        loaded.current = true;
      } catch (e) {
        toast.error("Design not found");
        navigate("/dashboard");
      }
    };
    load();
  }, [id, navigate]);

  // ---- Compute fit scale ----
  useEffect(() => {
    if (!design || !viewportRef.current) return;
    const compute = () => {
      const vp = viewportRef.current;
      if (!vp) return;
      const pad = 96;
      const sx = (vp.clientWidth - pad) / design.width;
      const sy = (vp.clientHeight - pad) / design.height;
      setFitScale(Math.min(sx, sy, 1));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [design, panelOpen]);

  // ---- Autosave (debounced) ----
  const pendingExtra = useRef({});
  const persist = useCallback((els, extra = {}) => {
    if (!loaded.current) return;
    setSaving(true);
    pendingExtra.current = { ...pendingExtra.current, ...extra };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = { elements: els, ...pendingExtra.current };
      pendingExtra.current = {};
      try {
        await api.put(`/designs/${id}`, payload);
      } catch (e) {}
      setSaving(false);
    }, 900);
  }, [id]);

  const pushHistory = useCallback(() => {
    past.current.push(JSON.parse(JSON.stringify(elements)));
    if (past.current.length > 60) past.current.shift();
    future.current = [];
  }, [elements]);

  const applyElements = useCallback((els, { save = true } = {}) => {
    setElements(els);
    if (save) persist(els);
  }, [persist]);

  const updateElement = useCallback((elId, patch) => {
    setElements((prev) => {
      const next = prev.map((e) => (e.id === elId ? { ...e, ...patch } : e));
      return next;
    });
  }, []);

  const commitElements = useCallback(() => {
    setElements((prev) => { persist(prev); return prev; });
  }, [persist]);

  // Wrap interaction start/commit to drive fast-render (simplify) during drag/resize.
  const handleInteractionStart = useCallback(() => { pushHistory(); setInteracting(true); }, [pushHistory]);
  const handleCommit = useCallback(() => { setInteracting(false); commitElements(); }, [commitElements]);

  // ---- Selection helpers ----
  const selected = useMemo(() => elements.find((e) => e.id === selectedId) || null, [elements, selectedId]);

  // Clear per-word text selection when switching elements.
  useEffect(() => { setWordSel(null); }, [selectedId]);

  // Apply a style (color and/or single stroke) to the currently selected word range.
  const applyWordStyle = useCallback((patch) => {
    if (!wordSel || wordSel.end <= wordSel.start) return;
    const elx = elements.find((e) => e.id === wordSel.elId);
    if (!elx) return;
    pushHistory();
    const runs = [...(elx.runs || []), { start: wordSel.start, end: wordSel.end, ...patch }];
    updateElement(wordSel.elId, { runs });
    commitElements();
  }, [wordSel, elements, pushHistory, updateElement, commitElements]);

  // Remove all per-word styling from an element.
  const clearWordStyles = useCallback((elId) => {
    pushHistory();
    updateElement(elId, { runs: [] });
    commitElements();
  }, [pushHistory, updateElement, commitElements]);
  const selectedEls = useMemo(() => elements.filter((e) => selectedIds.includes(e.id)), [elements, selectedIds]);
  const multiSelected = selectedIds.length > 1;

  // Canvas click selection — expands to the whole group, supports additive (shift/ctrl).
  const selectFromCanvas = useCallback((id, additive) => {
    const el = elements.find((e) => e.id === id);
    const base = el && el.groupId ? elements.filter((e) => e.groupId === el.groupId).map((e) => e.id) : [id];
    setSelectedIds((prev) => {
      if (!additive) return base;
      const set = new Set(prev);
      const allIn = base.every((b) => set.has(b));
      base.forEach((b) => (allIn ? set.delete(b) : set.add(b)));
      return [...set];
    });
  }, [elements]);

  // ---- Group move (drag several layers together) ----
  const handleGroupDragStart = useCallback(() => {
    pushHistory();
    setInteracting(true);
    groupOriginRef.current = elements.filter((e) => selectedIds.includes(e.id)).map((e) => ({ id: e.id, x: e.x, y: e.y }));
  }, [elements, selectedIds, pushHistory]);

  const handleGroupMove = useCallback((dx, dy) => {
    const origins = groupOriginRef.current;
    if (!origins) return;
    setElements((prev) => prev.map((e) => {
      const o = origins.find((o) => o.id === e.id);
      return o ? { ...e, x: Math.round(o.x + dx), y: Math.round(o.y + dy) } : e;
    }));
  }, []);

  // ---- Multi-select group actions ----
  const groupSelected = useCallback(() => {
    if (selectedIds.length < 2) return;
    pushHistory();
    const gid = uid();
    applyElements(elements.map((e) => selectedIds.includes(e.id) ? { ...e, groupId: gid } : e));
  }, [selectedIds, elements, pushHistory, applyElements]);

  const ungroupSelected = useCallback(() => {
    pushHistory();
    applyElements(elements.map((e) => selectedIds.includes(e.id) ? { ...e, groupId: undefined } : e));
  }, [selectedIds, elements, pushHistory, applyElements]);

  const setLockMulti = useCallback((lockVal) => {
    pushHistory();
    applyElements(elements.map((e) => selectedIds.includes(e.id) ? { ...e, locked: lockVal } : e));
  }, [selectedIds, elements, pushHistory, applyElements]);

  const duplicateMulti = useCallback(() => {
    if (selectedIds.length === 0) return;
    pushHistory();
    const maxZ = elements.reduce((m, e) => Math.max(m, e.zIndex || 1), 0);
    const gidMap = {};
    const copies = selectedEls.map((e, i) => {
      let gid = e.groupId;
      if (gid) { gidMap[gid] = gidMap[gid] || uid(); gid = gidMap[gid]; }
      return { ...e, id: uid(), x: e.x + 24, y: e.y + 24, zIndex: maxZ + 1 + i, groupId: gid };
    });
    applyElements([...elements, ...copies]);
    setSelectedIds(copies.map((c) => c.id));
  }, [selectedIds, selectedEls, elements, pushHistory, applyElements]);

  const alignMulti = useCallback((mode) => {
    if (selectedIds.length < 2) return;
    pushHistory();
    const els = selectedEls;
    const minX = Math.min(...els.map((e) => e.x));
    const maxX = Math.max(...els.map((e) => e.x + e.width));
    const minY = Math.min(...els.map((e) => e.y));
    const maxY = Math.max(...els.map((e) => e.y + e.height));
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    applyElements(elements.map((e) => {
      if (!selectedIds.includes(e.id)) return e;
      if (mode === "left") return { ...e, x: Math.round(minX) };
      if (mode === "right") return { ...e, x: Math.round(maxX - e.width) };
      if (mode === "centerH") return { ...e, x: Math.round(cx - e.width / 2) };
      if (mode === "top") return { ...e, y: Math.round(minY) };
      if (mode === "bottom") return { ...e, y: Math.round(maxY - e.height) };
      if (mode === "middleV") return { ...e, y: Math.round(cy - e.height / 2) };
      return e;
    }));
  }, [selectedIds, selectedEls, elements, pushHistory, applyElements]);

  // ---- Alignment snapping ----
  const computeSnap = useCallback((movingId, x, y, w, h) => {
    const threshold = 6 / scale;
    const W = design.width, H = design.height;
    const vTargets = [0, W / 2, W];
    const hTargets = [0, H / 2, H];
    elements.forEach((e) => {
      if (e.id === movingId) return;
      vTargets.push(e.x, e.x + e.width / 2, e.x + e.width);
      hTargets.push(e.y, e.y + e.height / 2, e.y + e.height);
    });
    let snappedX = x, snappedY = y;
    const vGuides = [], hGuides = [];
    let bestV = null;
    [{ pos: x, off: 0 }, { pos: x + w / 2, off: w / 2 }, { pos: x + w, off: w }].forEach((a) => {
      vTargets.forEach((t) => {
        const d = Math.abs(a.pos - t);
        if (d <= threshold && (!bestV || d < bestV.d)) bestV = { d, x: t - a.off, line: t };
      });
    });
    if (bestV) { snappedX = bestV.x; vGuides.push(bestV.line); }
    let bestH = null;
    [{ pos: y, off: 0 }, { pos: y + h / 2, off: h / 2 }, { pos: y + h, off: h }].forEach((a) => {
      hTargets.forEach((t) => {
        const d = Math.abs(a.pos - t);
        if (d <= threshold && (!bestH || d < bestH.d)) bestH = { d, y: t - a.off, line: t };
      });
    });
    if (bestH) { snappedY = bestH.y; hGuides.push(bestH.line); }
    return { x: snappedX, y: snappedY, guides: { v: vGuides, h: hGuides } };
  }, [elements, design, scale]);

  const addElement = (el) => {
    pushHistory();
    const maxZ = elements.reduce((m, e) => Math.max(m, e.zIndex || 1), 0);
    const placed = { ...el, x: el.x ?? 100, y: el.y ?? 100, zIndex: maxZ + 1 };
    const next = [...elements, placed];
    applyElements(next);
    setSelectedId(placed.id);
  };

  const duplicateSelected = () => {
    if (!selected) return;
    pushHistory();
    const copy = { ...selected, id: uid(), x: selected.x + 24, y: selected.y + 24, zIndex: (selected.zIndex || 1) + 1 };
    applyElements([...elements, copy]);
    setSelectedId(copy.id);
  };

  const deleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return;
    pushHistory();
    applyElements(elements.filter((e) => !selectedIds.includes(e.id)));
    setSelectedIds([]);
  }, [selectedIds, elements, pushHistory, applyElements]);

  const nudge = (dx, dy) => {
    if (selectedIds.length === 0) return;
    const now = Date.now();
    if (now - lastNudgeRef.current > 400) pushHistory();
    lastNudgeRef.current = now;
    applyElements(elements.map((e) => selectedIds.includes(e.id) && !e.locked ? { ...e, x: e.x + dx, y: e.y + dy } : e));
  };

  const layerSelected = (dir) => {
    if (!selected) return;
    pushHistory();
    const sorted = [...elements].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const idx = sorted.findIndex((e) => e.id === selected.id);
    const swapWith = dir === "up" ? idx + 1 : idx - 1;
    if (swapWith < 0 || swapWith >= sorted.length) return;
    const a = sorted[idx], b = sorted[swapWith];
    const za = a.zIndex, zb = b.zIndex;
    applyElements(elements.map((e) => e.id === a.id ? { ...e, zIndex: zb } : e.id === b.id ? { ...e, zIndex: za } : e));
  };

  const undo = () => {
    if (past.current.length === 0) return;
    future.current.push(JSON.parse(JSON.stringify(elements)));
    const prev = past.current.pop();
    setElements(prev);
    persist(prev);
    setSelectedId(null);
    forceRerender((n) => n + 1);
  };
  const redo = () => {
    if (future.current.length === 0) return;
    past.current.push(JSON.parse(JSON.stringify(elements)));
    const next = future.current.pop();
    setElements(next);
    persist(next);
    forceRerender((n) => n + 1);
  };

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    const handler = (e) => {
      if (editingText) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "h") { e.preventDefault(); setFindReplaceOpen(true); return; }
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target.isContentEditable) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedIds.length) { e.preventDefault(); deleteSelected(); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") { e.preventDefault(); selectedIds.length > 1 ? duplicateMulti() : duplicateSelected(); }
      if (selectedIds.length && !e.ctrlKey && !e.metaKey && e.key.startsWith("Arrow")) {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        if (e.key === "ArrowLeft") nudge(-step, 0);
        else if (e.key === "ArrowRight") nudge(step, 0);
        else if (e.key === "ArrowUp") nudge(0, -step);
        else if (e.key === "ArrowDown") nudge(0, step);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  // ---- Paste image from clipboard (copied from the web, screenshots, etc.) ----
  const addImageFromUpload = async (storagePath) => {
    const url = fileUrl(storagePath);
    let w = 600, h = 400;
    try { const img = await loadImage(url); w = img.naturalWidth || 600; h = img.naturalHeight || 400; } catch (e) {}
    const r = Math.min((design.width * 0.6) / w, (design.height * 0.6) / h, 1);
    w = Math.round(w * r); h = Math.round(h * r);
    addElement(newImageElement(url, { x: Math.round((design.width - w) / 2), y: Math.round((design.height - h) / 2), width: w, height: h }));
  };

  const pasteImageBlob = async (blob) => {
    toast.loading("Pasting image…", { id: "paste" });
    try {
      const ext = (blob.type.split("/")[1] || "png").replace("jpeg", "jpg").replace("svg+xml", "png");
      const form = new FormData();
      form.append("file", blob, `pasted.${ext}`);
      const res = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await addImageFromUpload(res.data.storage_path);
      toast.success("Image pasted", { id: "paste" });
    } catch (e) { toast.error("Couldn't paste that image", { id: "paste" }); }
  };

  const pasteImageUrl = async (url) => {
    toast.loading("Importing image…", { id: "paste" });
    try {
      const res = await api.post("/images/import-url", { url });
      await addImageFromUpload(res.data.storage_path);
      toast.success("Image added", { id: "paste" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import that image link", { id: "paste" });
    }
  };

  useEffect(() => {
    const onPaste = async (e) => {
      if (editingText) return;
      const ae = document.activeElement;
      if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable)) return;
      const items = (e.clipboardData && e.clipboardData.items) || [];
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          const blob = it.getAsFile();
          if (blob) { e.preventDefault(); await pasteImageBlob(blob); return; }
        }
      }
      const text = e.clipboardData && e.clipboardData.getData("text");
      if (text && /^https?:\/\/\S+/i.test(text.trim())) { e.preventDefault(); await pasteImageUrl(text.trim()); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  const setBackground = (color) => {
    pushHistory();
    setDesign((d) => ({ ...d, background: color }));
    persist(elements, { background: color });
  };

  const setAudioTrack = (track) => {
    const value = track && track.storage_path ? track : {};
    setDesign((d) => ({ ...d, audio: value }));
    persist(elements, { audio: value });
  };

  const applyTemplate = (tmpl) => {
    pushHistory();
    const newEls = JSON.parse(JSON.stringify(tmpl.elements));
    setDesign((d) => ({ ...d, background: tmpl.background, width: tmpl.width, height: tmpl.height }));
    setElements(newEls);
    persist(newEls, { background: tmpl.background, width: tmpl.width, height: tmpl.height });
    setSelectedId(null);
    toast.success("Template applied");
  };

  // ---- Export ----
  const fontCssCache = useRef(null);

  // Force every font actually used by text layers to fully download/decode before snapshotting.
  const ensureFontsLoaded = useCallback(async () => {
    try {
      const families = new Map();
      elements.forEach((e) => {
        if (e.type === "text" && e.fontFamily) {
          const key = `${e.fontWeight || 400}|${e.fontFamily}`;
          const sample = (families.get(key) || "") + (e.text || "Ag");
          families.set(key, sample.slice(0, 400) || "Ag");
        }
      });
      await Promise.all([...families.entries()].map(([key, sample]) => {
        const [weight, family] = key.split("|");
        return document.fonts.load(`${weight} 64px '${family}'`, sample).catch(() => {});
      }));
      await document.fonts.ready;
    } catch (e) {}
  }, [elements]);

  // Build & cache the inlined @font-face CSS so html-to-image bakes real fonts into the snapshot.
  const getFontEmbedCSS = useCallback(async () => {
    if (fontCssCache.current != null) return fontCssCache.current;
    try {
      fontCssCache.current = artboardRef.current ? await htmlToImage.getFontEmbedCSS(artboardRef.current) : "";
    } catch (e) { fontCssCache.current = ""; }
    return fontCssCache.current;
  }, []);

  // Invalidate the cached font CSS whenever the set of fonts in use changes.
  const usedFontsKey = useMemo(
    () => [...new Set(elements.filter((e) => e.type === "text" && e.fontFamily).map((e) => `${e.fontFamily}|${e.fontWeight || 400}`))].sort().join(","),
    [elements]
  );
  useEffect(() => { fontCssCache.current = null; }, [usedFontsKey]);

  const renderDataUrl = async (format) => {
    const node = artboardRef.current;
    const pixelRatio = 1 / scale; // upscale to full design resolution
    await ensureFontsLoaded();
    const fontEmbedCSS = await getFontEmbedCSS();
    const opts = {
      pixelRatio,
      backgroundColor: design.background,
      cacheBust: false,
      width: design.width * scale,
      height: design.height * scale,
      fontEmbedCSS,
    };
    // Primary: html-to-image (preserves CSS filters / drop-shadow / text effects + embedded fonts)
    try {
      if (format === "jpg") return await htmlToImage.toJpeg(node, { ...opts, quality: 0.95 });
      return await htmlToImage.toPng(node, opts);
    } catch (primaryErr) {
      // Fallback: html2canvas (fonts already loaded above)
      const canvas = await html2canvas(node, { scale: pixelRatio, useCORS: true, backgroundColor: design.background, logging: false });
      return canvas.toDataURL(format === "jpg" ? "image/jpeg" : "image/png", 0.95);
    }
  };

  const addWatermark = (dataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width; c.height = img.height;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const pad = Math.round(Math.min(c.width, c.height) * 0.022) + 6;
      const fs = Math.max(14, Math.round(Math.min(c.width, c.height) * 0.028));
      ctx.font = `600 ${fs}px Outfit, Arial, sans-serif`;
      ctx.textBaseline = "bottom";
      ctx.textAlign = "right";
      const text = "Made with FlyerStudio";
      const tw = ctx.measureText(text).width;
      const bx = c.width - tw - pad * 2.2, by = c.height - fs - pad * 1.4;
      ctx.fillStyle = "rgba(0,0,0,0.42)";
      ctx.fillRect(bx, by, tw + pad * 1.6, fs + pad * 0.9);
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      ctx.fillText(text, c.width - pad * 1.2, c.height - pad * 0.7);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

  const exportDesign = async (format) => {
    if (!design) return;
    if (format === "pdf" && !isPro) {
      openUpgrade("PDF export is a Pro feature. Upgrade to download print-ready PDFs.");
      return;
    }
    // Server-side authorization for PDF export (defense in depth — the client-side
    // isPro flag can be tampered with, so the server has final say).
    if (format === "pdf") {
      try {
        await api.post("/exports/pdf/authorize");
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = (detail && typeof detail === "object" ? detail.message : detail) ||
                    "PDF export is a Pro feature. Upgrade to download print-ready PDFs.";
        openUpgrade(msg);
        return;
      }
    }
    const prevSel = selectedIds;
    setSelectedIds([]);
    // Snapshot current frame of any video overlays so static exports aren't blank.
    const posters = {};
    const vids = artboardRef.current ? artboardRef.current.querySelectorAll("video") : [];
    vids.forEach((v) => {
      const root = v.closest('[data-testid^="canvas-el-"]');
      const elId = root && root.getAttribute("data-testid").replace("canvas-el-", "");
      if (!elId) return;
      try {
        const c = document.createElement("canvas");
        c.width = v.videoWidth || v.clientWidth;
        c.height = v.videoHeight || v.clientHeight;
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        posters[elId] = c.toDataURL("image/png");
      } catch (e) {}
    });
    setVideoPosters(posters);
    setExporting(true);
    await new Promise((r) => setTimeout(r, 120));
    toast.loading("Preparing your download…", { id: "export" });
    try {
      const imgData = await renderDataUrl(format === "pdf" ? "png" : format);
      if (format === "pdf") {
        const pdf = new jsPDF({ orientation: design.width > design.height ? "landscape" : "portrait", unit: "px", format: [design.width, design.height] });
        pdf.addImage(imgData, "PNG", 0, 0, design.width, design.height);
        pdf.save(`${design.title || "design"}.pdf`);
      } else {
        const finalData = isPro ? imgData : await addWatermark(imgData);
        const link = document.createElement("a");
        link.download = `${design.title || "design"}.${format}`;
        link.href = finalData;
        link.click();
      }
      toast.success("Downloaded!", { id: "export" });
    } catch (e) {
      toast.error("Export failed", { id: "export" });
    } finally {
      setExporting(false);
      setVideoPosters({});
      setSelectedIds(prevSel);
    }
  };

  // Flatten the current design to a clean PNG data URL (no watermark) — used by "Save to Styles".
  const captureFlattenedPng = async () => {
    if (!design) return null;
    const prevSel = selectedIds;
    setSelectedIds([]);
    const posters = {};
    const vids = artboardRef.current ? artboardRef.current.querySelectorAll("video") : [];
    vids.forEach((v) => {
      const root = v.closest('[data-testid^="canvas-el-"]');
      const elId = root && root.getAttribute("data-testid").replace("canvas-el-", "");
      if (!elId) return;
      try {
        const c = document.createElement("canvas");
        c.width = v.videoWidth || v.clientWidth;
        c.height = v.videoHeight || v.clientHeight;
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        posters[elId] = c.toDataURL("image/png");
      } catch (e) {}
    });
    setVideoPosters(posters);
    setExporting(true);
    await new Promise((r) => setTimeout(r, 120));
    try {
      return await renderDataUrl("png");
    } finally {
      setExporting(false);
      setVideoPosters({});
      setSelectedIds(prevSel);
    }
  };

  const exportVideo = async (format) => {
    if (!design) return;
    if (!isPro) {
      openUpgrade("Export as Video is a Pro feature. Upgrade to export motion flyers with sound.");
      return;
    }
    const videoEls = elements.filter((e) => e.type === "video" && e.src && !e.hidden);
    const hasAudio = design.audio && design.audio.storage_path;
    if (videoEls.length === 0 && !hasAudio) {
      toast.error("Add an uploaded video or a background audio track first.");
      return;
    }
    // Compute duration from element/audio durations, fallback to live <video> metadata.
    let duration = 0;
    videoEls.forEach((e) => { if (e.duration) duration = Math.max(duration, e.duration); });
    if (hasAudio && design.audio.duration) duration = Math.max(duration, design.audio.duration);
    if (duration < 1 && artboardRef.current) {
      artboardRef.current.querySelectorAll("video").forEach((v) => {
        if (v.duration && isFinite(v.duration)) duration = Math.max(duration, v.duration);
      });
    }
    duration = Math.max(1, Math.min(duration || 10, 120));

    const pathOf = (src) => (src && src.includes("/files/")) ? src.split("/files/")[1] : src;
    const videos = videoEls.map((e) => ({
      storage_path: pathOf(e.src),
      x: Math.round(e.x), y: Math.round(e.y), w: Math.round(e.width), h: Math.round(e.height),
      muted: e.muted !== false, volume: e.volume ?? 1,
      loop: e.playOnce ? false : (e.loop !== false), dur: e.duration || 0,
    }));

    const prevSel = selectedIds;
    setSelectedIds([]);
    setVideoBaseCapture(true);
    setExporting(true);
    await new Promise((r) => setTimeout(r, 180));
    toast.loading("Rendering your video… this can take a minute.", { id: "vexport" });
    let basePng;
    try {
      await ensureFontsLoaded();
      const fontEmbedCSS = await getFontEmbedCSS();
      basePng = await htmlToImage.toPng(artboardRef.current, {
        pixelRatio: 1 / scale, backgroundColor: undefined, cacheBust: false,
        width: design.width * scale, height: design.height * scale, fontEmbedCSS,
      });
    } catch (e) {
      toast.error("Could not render the frame", { id: "vexport" });
      setVideoBaseCapture(false); setExporting(false); setSelectedIds(prevSel);
      return;
    }
    setVideoBaseCapture(false);
    setSelectedIds(prevSel);

    try {
      const res = await api.post("/export/video", {
        format, width: design.width, height: design.height,
        bg_color: design.background || "#000000", duration,
        base_png: basePng, videos,
        audio: hasAudio ? { storage_path: design.audio.storage_path, volume: design.audio.volume ?? 1, loop: design.audio.loop !== false, dur: design.audio.duration || 0 } : null,
      });
      const jobId = res.data.id;
      const ready = await new Promise((resolve, reject) => {
        let tries = 0;
        const tick = async () => {
          tries += 1;
          try {
            const s = await api.get(`/export/status/${jobId}`);
            if (s.data.status === "ready") return resolve(s.data);
            if (s.data.status === "error") return reject(new Error(s.data.error || "Render failed"));
          } catch (err) {}
          if (tries >= 150) return reject(new Error("Timed out"));
          setTimeout(tick, 3000);
        };
        tick();
      });
      const fname = `${(design.title || "design").replace(/[^\w\-]+/g, "_")}.${format}`;
      const dlUrl = `${fileUrl(ready.storage_path)}?download=1&filename=${encodeURIComponent(fname)}`;

      // Reliable download: backend sends Content-Disposition: attachment, so opening
      // the URL forces a file download (not navigation). Try blob first, then window.open.
      const triggerDownload = async () => {
        try {
          const resp = await fetch(dlUrl);
          if (resp.ok) {
            const blob = await resp.blob();
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl; a.download = fname;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
            return true;
          }
        } catch (err) { /* fall through */ }
        // Fallback: open the attachment URL directly (forces download via header).
        window.open(dlUrl, "_blank");
        return false;
      };

      await triggerDownload();

      // Persistent manual button — a real user gesture is never blocked by Chrome.
      toast.success("Your video is ready!", {
        id: "vexport",
        duration: 600000,
        description: "If the download didn't start, click below.",
        action: { label: "Download video", onClick: () => { window.open(dlUrl, "_blank"); } },
      });
    } catch (e) {
      if (e.response?.status === 402) openUpgrade(e.response?.data?.detail || "Upgrade to export video.");
      else toast.error(e.message || "Video export failed", { id: "vexport" });
      toast.dismiss("vexport");
    } finally {
      setExporting(false);
    }
  };

  // ---- Thumbnail on save-out ----
  const generateThumbnail = useCallback(async () => {
    if (!artboardRef.current) return null;
    try {
      await ensureFontsLoaded();
      const fontEmbedCSS = await getFontEmbedCSS();
      return await htmlToImage.toJpeg(artboardRef.current, {
        quality: 0.7,
        backgroundColor: design.background,
        width: design.width * scale,
        height: design.height * scale,
        canvasWidth: 360,
        canvasHeight: Math.round((360 * design.height) / design.width),
        fontEmbedCSS,
        cacheBust: false,
      });
    } catch (e) { return null; }
  }, [design, scale, ensureFontsLoaded, getFontEmbedCSS]);

  // Auto-save a fresh thumbnail — runs only when idle, never during drag/resize/export,
  // skips if nothing meaningful changed, and yields to the browser to avoid UI freezes.
  useEffect(() => {
    if (!loaded.current || interacting || exporting) return;
    const sig = elements.map((e) => `${e.id}:${Math.round(e.x)},${Math.round(e.y)},${Math.round(e.width)},${Math.round(e.height)},${e.zIndex || 0},${e.hidden ? 1 : 0},${(e.text || "").length},${e.fontFamily || ""},${e.src || ""}`).join("|") + `#${design?.background}`;
    if (sig === lastThumbSig.current) return;
    if (thumbTimer.current) clearTimeout(thumbTimer.current);
    thumbTimer.current = setTimeout(() => {
      const run = async () => {
        if (thumbBusy.current || interacting || exporting) return;
        thumbBusy.current = true;
        try {
          const thumb = await generateThumbnail();
          if (thumb) { lastThumbSig.current = sig; await api.put(`/designs/${id}`, { thumbnail: thumb }); }
        } catch (e) {} finally { thumbBusy.current = false; }
      };
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 3000 });
      else run();
    }, 6000);
    return () => { if (thumbTimer.current) clearTimeout(thumbTimer.current); };
  }, [elements, design?.background, generateThumbnail, id, interacting, exporting]);

  const shareDesign = async () => {
    if (!isPro) {
      openUpgrade("Shareable links are a Pro feature. Upgrade to share live design links with clients.");
      return;
    }
    setSelectedId(null);
    const thumb = await generateThumbnail();
    try {
      const res = await api.post(`/designs/${id}/share`);
      if (thumb) await api.put(`/designs/${id}`, { thumbnail: thumb });
      const link = `${window.location.origin}/share/${res.data.share_id}`;
      if (res.data.is_public) {
        await navigator.clipboard.writeText(link).catch(() => {});
        toast.success("Share link copied to clipboard!", { description: link });
      } else {
        toast.message("Sharing turned off for this design");
      }
    } catch (e) {
      if (e.response?.status === 402) {
        openUpgrade(e.response?.data?.detail || "Shareable links are a Pro feature.");
      } else {
        toast.error("Could not create share link");
      }
    }
  };

  const loadImage = (src) => new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });

  const legacyExportVideo = async (size, durationSec) => {
    if (!design || videoExporting) return;
    const candidates = [
      "video/mp4;codecs=avc1.4d002a", "video/mp4",
      "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm",
    ];
    const mime = (typeof MediaRecorder !== "undefined")
      ? candidates.find((c) => MediaRecorder.isTypeSupported(c)) : null;
    if (!mime) { toast.error("Your browser can't export video. Try Chrome or Edge."); return; }
    const ext = mime.includes("mp4") ? "mp4" : "webm";

    setVideoExporting(true);
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 60));
    toast.loading("Rendering video…", { id: "vid" });
    try {
      const node = artboardRef.current;
      await ensureFontsLoaded();
      const fontEmbedCSS = await getFontEmbedCSS();

      const W = size.width, H = size.height;
      const s = Math.min(W / design.width, H / design.height);
      const offX = (W - design.width * s) / 2;
      const offY = (H - design.height * s) / 2;

      // Map video elements to their live DOM <video> nodes
      const vids = (elements || [])
        .filter((e) => e.type === "video" && !e.hidden)
        .map((e) => {
          const v = node.querySelector(`[data-testid="canvas-el-${e.id}"] video`);
          return v ? { el: e, video: v } : null;
        })
        .filter(Boolean);

      // Build a z-ordered sequence so videos AND blend-mode layers composite in their
      // real position. Normal static layers are grouped into "bands" (one snapshot each);
      // blend-mode layers and videos are individual items so their blend applies against
      // everything beneath them at canvas-composite time (e.g. Screen drops black flares).
      const vmap = new Map(vids.map((v) => [v.el.id, v]));
      const ordered = [...elements].filter((e) => !e.hidden).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
      const blended = (e) => e.blendMode && e.blendMode !== "normal";
      const sequence = [];
      let curBand = [];
      const flush = () => { if (curBand.length) sequence.push({ type: "band", ids: curBand }); curBand = []; };
      ordered.forEach((e) => {
        if (e.type === "video" && vmap.has(e.id)) {
          flush();
          sequence.push({ type: "video", ...vmap.get(e.id) });
        } else if (blended(e)) {
          flush();
          sequence.push({ type: "element", id: e.id, blendMode: e.blendMode });
        } else {
          curBand.push(e.id);
        }
      });
      flush();

      // Snapshot bands / individual blended layers as transparent PNGs.
      // Blend modes are neutralised during capture so we apply them ourselves at composite time.
      const allNodes = Array.from(node.querySelectorAll('[data-testid^="canvas-el-"]'));
      const prevDisplay = allNodes.map((n) => n.style.display);
      const prevBlend = allNodes.map((n) => n.style.mixBlendMode);
      const prevBg = node.style.background;
      node.style.background = "transparent";
      const snapStatics = async (ids) => {
        const idset = new Set(ids);
        allNodes.forEach((n) => {
          const tid = n.getAttribute("data-testid").replace("canvas-el-", "");
          const show = idset.has(tid);
          n.style.display = show ? "" : "none";
          if (show) n.style.mixBlendMode = "normal";
        });
        let url;
        try {
          url = await htmlToImage.toPng(node, {
            pixelRatio: 1 / scale, width: design.width * scale, height: design.height * scale,
            backgroundColor: undefined, cacheBust: false, fontEmbedCSS,
          });
        } catch (primaryErr) {
          const cv = await html2canvas(node, { scale: 1 / scale, useCORS: true, backgroundColor: null, logging: false });
          url = cv.toDataURL("image/png");
        }
        return loadImage(url);
      };
      try {
        for (const item of sequence) {
          if (item.type === "band") item.img = item.ids.length ? await snapStatics(item.ids) : null;
          else if (item.type === "element") item.img = await snapStatics([item.id]);
        }
      } finally {
        allNodes.forEach((n, i) => { n.style.display = prevDisplay[i]; n.style.mixBlendMode = prevBlend[i]; });
        node.style.background = prevBg;
      }

      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");

      await Promise.all(vids.map(({ video }) =>
        video.readyState >= 2 ? Promise.resolve()
          : new Promise((res) => { video.oncanplay = res; setTimeout(res, 1500); })));
      vids.forEach(({ video }) => { try { video.currentTime = 0; video.play(); } catch (e) {} });

      const draw = () => {
        ctx.fillStyle = design.background; ctx.fillRect(0, 0, W, H);
        for (const item of sequence) {
          if (item.type === "band") {
            if (item.img) ctx.drawImage(item.img, offX, offY, design.width * s, design.height * s);
          } else if (item.type === "element") {
            if (!item.img) continue;
            ctx.save();
            ctx.globalCompositeOperation = item.blendMode || "source-over";
            ctx.drawImage(item.img, offX, offY, design.width * s, design.height * s);
            ctx.restore();
          } else {
            const { el, video } = item;
            const w = el.width * s, h = el.height * s;
            const cx = (el.x + el.width / 2) * s + offX;
            const cy = (el.y + el.height / 2) * s + offY;
            ctx.save();
            ctx.globalAlpha = el.opacity ?? 1;
            ctx.globalCompositeOperation = el.blendMode && el.blendMode !== "normal" ? el.blendMode : "source-over";
            ctx.translate(cx, cy);
            if (el.rotation) ctx.rotate((el.rotation * Math.PI) / 180);
            try { ctx.drawImage(video, -w / 2, -h / 2, w, h); } catch (e) {}
            ctx.restore();
          }
        }
      };

      draw();
      const stream = canvas.captureStream(30);
      const chunks = [];
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8000000 });
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      const stopped = new Promise((res) => { rec.onstop = res; });

      rec.start();
      const startT = performance.now();
      const durMs = durationSec * 1000;
      await new Promise((resolve) => {
        const loop = (now) => {
          draw();
          if (now - startT < durMs) requestAnimationFrame(loop);
          else { rec.stop(); resolve(); }
        };
        requestAnimationFrame(loop);
      });
      await stopped;

      const blob = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.download = `${design.title || "design"}-${size.id}.${ext}`;
      a.href = url;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success(`Video downloaded (${ext.toUpperCase()})`, { id: "vid" });
      setVideoOpen(false);
    } catch (e) {
      toast.error("Video export failed", { id: "vid" });
    } finally {
      setVideoExporting(false);
    }
  };

  const handleResize = async (size) => {
    if (!design) return;
    const scaled = scaleElements(elements, design.width, design.height, size.width, size.height);
    try {
      const res = await api.post("/designs", {
        title: `${design.title} — ${size.label}`,
        category: size.category || design.category || "flyer",
        width: size.width,
        height: size.height,
        background: design.background,
        elements: scaled,
      });
      toast.success(`Resized copy created — ${size.label}`);
      setResizeOpen(false);
      loaded.current = false;
      navigate(`/editor/${res.data.id}`);
    } catch (e) {
      toast.error("Could not resize design");
    }
  };

  const toggleVisibility = useCallback((id) => {
    pushHistory();
    applyElements(elements.map((e) => e.id === id ? { ...e, hidden: !e.hidden } : e));
  }, [elements, pushHistory, applyElements]);

  const toggleLock = useCallback((id) => {
    pushHistory();
    applyElements(elements.map((e) => e.id === id ? { ...e, locked: !e.locked } : e));
  }, [elements, pushHistory, applyElements]);

  const renameLayer = useCallback((id, name) => {
    applyElements(elements.map((e) => e.id === id ? { ...e, name: name || undefined } : e));
  }, [elements, applyElements]);

  const updateLayerText = useCallback((id, text) => {
    pushHistory();
    applyElements(elements.map((e) => e.id === id ? { ...e, text } : e));
  }, [elements, applyElements, pushHistory]);

  const replaceText = useCallback((find, replace, opts = {}) => {
    if (!find) return 0;
    const esc = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(esc, opts.matchCase ? "g" : "gi");
    const targets = opts.selectedOnly && selectedIds.length ? new Set(selectedIds) : null;
    let count = 0;
    const next = elements.map((e) => {
      if (e.type !== "text" || !e.text) return e;
      if (targets && !targets.has(e.id)) return e;
      const m = e.text.match(re);
      if (!m) return e;
      count += m.length;
      return { ...e, text: e.text.replace(re, replace) };
    });
    if (count > 0) { pushHistory(); applyElements(next); }
    return count;
  }, [elements, selectedIds, applyElements, pushHistory]);

  const reorderLayers = useCallback((frontToBack) => {
    pushHistory();
    const n = frontToBack.length;
    const zmap = {};
    frontToBack.forEach((el, i) => { zmap[el.id] = n - i; });
    applyElements(elements.map((e) => zmap[e.id] != null ? { ...e, zIndex: zmap[e.id] } : e));
  }, [elements, pushHistory, applyElements]);

  // ---- Layer folders ----
  const createFolder = useCallback((memberIds = []) => {
    const fid = uid();
    const used = folders.map((f) => parseInt((f.name.match(/^Folder (\d+)$/) || [])[1], 10)).filter((n) => !isNaN(n));
    const next = used.length ? Math.max(...used) + 1 : 1;
    const name = `Folder ${next}`;
    const nextFolders = [...folders, { id: fid, name, collapsed: false }];
    setFolders(nextFolders);
    if (memberIds.length) {
      pushHistory();
      const nextEls = elements.map((e) => memberIds.includes(e.id) ? { ...e, folderId: fid } : e);
      setElements(nextEls);
      persist(nextEls, { folders: nextFolders });
    } else {
      persist(elements, { folders: nextFolders });
    }
    return fid;
  }, [folders, elements, pushHistory, persist]);

  const renameFolder = useCallback((fid, name) => {
    const nextFolders = folders.map((f) => f.id === fid ? { ...f, name: name || f.name } : f);
    setFolders(nextFolders);
    persist(elements, { folders: nextFolders });
  }, [folders, elements, persist]);

  const deleteFolder = useCallback((fid) => {
    pushHistory();
    const nextFolders = folders.filter((f) => f.id !== fid);
    const nextEls = elements.map((e) => e.folderId === fid ? { ...e, folderId: undefined } : e);
    setFolders(nextFolders);
    setElements(nextEls);
    persist(nextEls, { folders: nextFolders });
  }, [folders, elements, pushHistory, persist]);

  const toggleFolderCollapse = useCallback((fid) => {
    const nextFolders = folders.map((f) => f.id === fid ? { ...f, collapsed: !f.collapsed } : f);
    setFolders(nextFolders);
    persist(elements, { folders: nextFolders });
  }, [folders, elements, persist]);

  const toggleFolderVisibility = useCallback((fid) => {
    pushHistory();
    const members = elements.filter((e) => e.folderId === fid);
    const anyVisible = members.some((e) => !e.hidden);
    applyElements(elements.map((e) => e.folderId === fid ? { ...e, hidden: anyVisible } : e));
  }, [elements, pushHistory, applyElements]);

  const assignToFolder = useCallback((ids, fid) => {
    pushHistory();
    applyElements(elements.map((e) => ids.includes(e.id) ? { ...e, folderId: fid || undefined } : e));
  }, [elements, pushHistory, applyElements]);

  if (!design) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
        <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  const sortedEls = [...elements].filter((e) => !e.hidden).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  const layersListLive = [...elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
  // Freeze the Layers panel during drag/resize so its image/video thumbnails don't reload every frame.
  if (!interacting) layersSnapshot.current = layersListLive;
  const layersList = interacting ? layersSnapshot.current : layersListLive;

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-[#0A0A0A] text-white font-body">
      <TopBar
        title={design.title}
        onTitleChange={(v) => { setDesign((d) => ({ ...d, title: v })); persist(elements, { title: v }); }}
        canUndo={past.current.length > 0}
        canRedo={future.current.length > 0}
        onUndo={undo}
        onRedo={redo}
        zoom={zoom}
        onZoomIn={() => setZoom((z) => Math.min(3, z + 0.1))}
        onZoomOut={() => setZoom((z) => Math.max(0.2, z - 0.1))}
        onExport={exportDesign}
        onShare={shareDesign}
        saving={saving}
        performanceMode={performanceMode}
        onTogglePerformance={togglePerformance}
        onResizeClick={() => setResizeOpen(true)}
        onFindReplace={() => setFindReplaceOpen(true)}
        onSaveTemplate={() => { setSaveTemplateOpen(true); generateThumbnail().then((t) => { if (t) api.put(`/designs/${id}`, { thumbnail: t }).catch(() => {}); }); }}
        isPro={isPro}
        onUpgrade={() => openUpgrade("Unlock watermark-free exports, PDF, share links and more.")}
        onExportVideo={exportVideo}
        isAdmin={!!user?.is_admin}
        onSaveToStyles={() => setSaveStylesOpen(true)}
      />

      <ResizeModal
        open={resizeOpen}
        onOpenChange={setResizeOpen}
        current={{ width: design.width, height: design.height }}
        onResize={handleResize}
      />

      <SaveTemplateModal
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        designId={id}
        defaultTitle={design.title}
        isAdmin={!!user?.is_admin}
      />

      {!!user?.is_admin && (
        <SaveToStylesModal
          open={saveStylesOpen}
          onOpenChange={setSaveStylesOpen}
          defaultTitle={design.title}
          capture={captureFlattenedPng}
        />
      )}

      <FindReplaceModal
        open={findReplaceOpen}
        onOpenChange={setFindReplaceOpen}
        texts={elements.filter((e) => e.type === "text").map((e) => ({ id: e.id, text: e.text }))}
        hasSelection={selectedIds.length > 0}
        onReplaceAll={replaceText}
      />

      <div className="flex-1 flex overflow-hidden">
        {/* Left rail */}
        <div className="w-[72px] flex flex-col items-center py-3 border-r border-white/10 bg-[#0A0A0A] gap-1 z-40 shrink-0">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => { setActiveTool(t.id); setPanelOpen(true); }}
              data-testid={`tool-${t.id}`}
              className={`w-14 py-2 rounded-xl flex flex-col items-center gap-1 transition-colors ${activeTool === t.id && panelOpen ? "bg-blue-600/20 text-blue-400" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}
            >
              <t.icon weight={activeTool === t.id && panelOpen ? "fill" : "regular"} size={22} />
              <span className="text-[10px] font-medium">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Left flyout panel */}
        {panelOpen && (
          <div className="w-80 border-r border-white/10 bg-[#121212] flex flex-col z-30 shrink-0">
            <div className="h-11 flex items-center justify-between px-4 border-b border-white/10">
              <span className="text-sm font-semibold capitalize">{activeTool}</span>
              <button onClick={() => setPanelOpen(false)} className="h-7 w-7 rounded-lg hover:bg-white/10 flex items-center justify-center" data-testid="close-panel-btn"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-hidden">
              {activeTool === "templates" && <TemplatesPanel templates={templates} onApply={applyTemplate} />}
              {activeTool === "elements" && <ElementsPanel onAdd={addElement} />}
              {activeTool === "text" && <TextPanel onAdd={addElement} />}
              {activeTool === "overlays" && <OverlaysPanel onAdd={addElement} />}
              {activeTool === "uploads" && <UploadsPanel onAdd={addElement} onSetAudio={setAudioTrack} audio={design?.audio} />}
              {activeTool === "ai" && <AIPanel onAdd={addElement} />}
            </div>
          </div>
        )}

        {/* Canvas viewport */}
        <div
          ref={viewportRef}
          className="flex-1 bg-[#1A1A1C] overflow-auto relative flex items-center justify-center p-12 custom-scrollbar"
          onMouseDown={(e) => { if (!e.shiftKey && !e.ctrlKey && !e.metaKey) setSelectedId(null); }}
          data-testid="editor-canvas-area"
        >
          <div
            ref={artboardRef}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: design.width * scale,
              height: design.height * scale,
              background: videoBaseCapture ? "transparent" : design.background,
              position: "relative",
              flexShrink: 0,
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
              isolation: "isolate",
            }}
            data-testid="artboard"
          >
            {sortedEls.map((el) => (
              <CanvasElement
                key={el.id}
                el={el}
                scale={scale}
                isSelected={selectedIds.includes(el.id)}
                interactive={!el.locked}
                multi={multiSelected && selectedIds.includes(el.id)}
                simplify={simplify}
                exportPoster={videoPosters[el.id]}
                hideMedia={videoBaseCapture}
                onSelect={(additive) => selectFromCanvas(el.id, additive)}
                onUpdate={(patch) => updateElement(el.id, patch)}
                onInteractionStart={handleInteractionStart}
                onGroupDragStart={handleGroupDragStart}
                onGroupMove={handleGroupMove}
                onCommit={handleCommit}
                onTextEditingChange={setEditingText}
                onWordSelect={setWordSel}
                computeSnap={computeSnap}
                onGuides={setGuides}
              />
            ))}
            {/* Alignment guides */}
            {guides.v.map((gx, i) => (
              <div key={`v${i}`} style={{ position: "absolute", left: gx * scale, top: 0, width: 1, height: "100%", background: "#EC4899", zIndex: 9999, pointerEvents: "none" }} />
            ))}
            {guides.h.map((gy, i) => (
              <div key={`h${i}`} style={{ position: "absolute", top: gy * scale, left: 0, height: 1, width: "100%", background: "#EC4899", zIndex: 9999, pointerEvents: "none" }} />
            ))}
          </div>
          <AudioTrackBar
            audio={design.audio}
            onChange={(patch) => setAudioTrack({ ...(design.audio || {}), ...patch })}
            onRemove={() => setAudioTrack(null)}
          />
        </div>

        {/* Photoshop-style Layers panel */}
        <LayersPanel
          layers={layersList}
          folders={folders}
          selectedIds={selectedIds}
          open={layersOpen}
          onToggleOpen={toggleLayersOpen}
          onSelectMany={setSelectedIds}
          onToggleVisibility={toggleVisibility}
          onToggleLock={toggleLock}
          onReorder={reorderLayers}
          onRename={renameLayer}
          onUpdateText={updateLayerText}
          onCreateFolder={createFolder}
          onRenameFolder={renameFolder}
          onDeleteFolder={deleteFolder}
          onToggleFolderCollapse={toggleFolderCollapse}
          onToggleFolderVisibility={toggleFolderVisibility}
          onAssignFolder={assignToFolder}
        />

        {/* Right properties panel */}
        <div className="w-72 border-l border-white/10 bg-[#121212] flex flex-col z-30 shrink-0 overflow-hidden">
          {multiSelected ? (
            <MultiSelectPanel
              count={selectedIds.length}
              grouped={selectedEls.length > 0 && selectedEls.every((e) => e.groupId && e.groupId === selectedEls[0].groupId)}
              allLocked={selectedEls.every((e) => e.locked)}
              onGroup={groupSelected}
              onUngroup={ungroupSelected}
              onLock={() => setLockMulti(true)}
              onUnlock={() => setLockMulti(false)}
              onDuplicate={duplicateMulti}
              onDelete={deleteSelected}
              onAlign={alignMulti}
            />
          ) : selected ? (
            <PropertiesPanel
              el={selected}
              onUpdate={(patch) => updateElement(selectedId, patch)}
              onStart={pushHistory}
              onCommit={commitElements}
              onDuplicate={duplicateSelected}
              onDelete={deleteSelected}
              onLayer={layerSelected}
              wordSel={wordSel && wordSel.elId === selectedId ? wordSel : null}
              onWordStyle={applyWordStyle}
              onWordSelect={setWordSel}
              onClearWordStyles={() => clearWordStyles(selectedId)}
            />
          ) : (
            <div className="p-4 h-full overflow-y-auto custom-scrollbar">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">Canvas</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Background color</p>
              <div className="flex items-center gap-2 mb-3">
                <label className="relative h-9 w-9 rounded-lg border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: design.background }}>
                  <input type="color" value={design.background} onChange={(e) => setBackground(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="bg-color-input" />
                </label>
                <input value={design.background} onChange={(e) => setBackground(e.target.value)} className="flex-1 h-9 rounded-lg bg-black/40 border border-white/10 px-2.5 text-sm text-white outline-none focus:border-blue-500/60" />
              </div>
              <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
                Tip: double-click any text to edit it. Drag to move, use corner handles to resize, and the top handle to rotate.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
